import { Box, Spinner, Text, VStack } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root';
import { useAppConfig } from '#providers/app-config-provider';

import {
  buildSettingsOAuthRedirect,
  parseTrelloCallbackPayload,
} from './trello-callback-helpers';

type TrelloCompleteResponse = {
  oauth?: string;
  oauthError?: string;
};

export function TrelloCallbackPage() {
  const navigate = useNavigate();
  const { config } = useAppConfig();

  useEffect(() => {
    const controller = new AbortController();
    const redirect = (params: { oauth?: string; oauthError?: string }) => {
      navigate(buildSettingsOAuthRedirect(params), { replace: true });
    };

    const { error, state, token } = parseTrelloCallbackPayload(
      window.location.search,
      window.location.hash,
    );

    if (error) {
      redirect({ oauthError: 'trello_denied' });
      return () => controller.abort();
    }

    if (!state || !token) {
      redirect({ oauthError: 'trello_invalid_request' });
      return () => controller.abort();
    }

    void (async () => {
      try {
        const res = await fetch(`${config.apiHost}/api/oauth/trello/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({ state, token }),
        });

        let payload: TrelloCompleteResponse | null = null;
        try {
          payload = (await res.json()) as TrelloCompleteResponse;
        } catch {
          payload = null;
        }

        if (res.ok && payload?.oauth === 'trello_success') {
          redirect({ oauth: 'trello_success' });
          return;
        }

        redirect({
          oauthError: payload?.oauthError ?? 'trello_callback_failed',
        });
      } catch {
        if (!controller.signal.aborted) {
          redirect({ oauthError: 'trello_callback_failed' });
        }
      }
    })();

    return () => controller.abort();
  }, [config.apiHost, navigate]);

  return (
    <PageContentRoot>
      <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={3} align="center">
          <Spinner size="sm" />
          <Text fontSize="sm" fontWeight="medium" color="fg">
            Finishing Trello connection…
          </Text>
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            This should only take a moment.
          </Text>
        </VStack>
      </Box>
    </PageContentRoot>
  );
}
